<template>
  <v-card flat>

    <v-card-text
      :class="!employeeCreate ? `` : `pa-0`"
    >
      <v-progress-linear
        v-if="!schema"
        height="2"
        class="ma-0"
        indeterminate/>
      <v-form
        v-if="schema && schema.days"
        ref="schemaForm"
        v-model="isFormValid"
        @keyup.native.enter="saveSchema">
        <v-layout row>
          <v-flex xs6>

            <v-subheader
              v-if="isDemo"
              class="v-subheader-margin-left">{{ $t('month') }}</v-subheader>

            <month-picker
              v-if="isDemo"
              v-model="month"
              :label="$t('month')"
              :rules="[$rules.isRequired]"
              :allowed-dates="allowedDates"
              :error-messages="errorMessages.month"
              class="mx-3"/>

            <v-subheader class="v-subheader-margin-left">{{ $t('baseOptions') }}</v-subheader>

            <v-switch
              v-if="!isDemo"
              :label="$t('perHourCalculation')"
              v-model="schema.per_hour_calc"
              :error-messages="errorMessages.per_hour_calc"
              :input-value="schema.per_hour_calc"
              :false-value="0"
              :true-value="1"
              class="mx-3"/>

            <employee-settings-salary-group
              v-if="schema.per_hour_calc"
              :is-demo="isDemo"
              class="mx-3"
              @onChange="onSalaryChange"
            />

            <v-text-field
              v-if="schema.per_hour_calc && !isDemo"
              v-model="schema.monthly_wage"
              :error-messages="errorMessages.monthly_wage"
              :rules="[!schema.per_hour_calc ? true : $rules.isRequired]"
              :label="$t('monthlyWage') + '*'"
              class="mx-3 mt-4"/>

            <v-text-field
              v-if="!schema.per_hour_calc"
              v-model="schema.hours_week"
              :label="$t('hoursWeek') + '*'"
              :error-messages="errorMessages.hours_week"
              :rules="[schema.per_hour_calc ? true : $rules.isRequired]"
              class="mx-3"
              type="number"
            />

            <v-text-field
              v-model.number="schema.allowed_leave_days"
              :error-messages="errorMessages.allowed_leave_days"
              :label="$t('allowedLeaveDays')"
              :false-value="0"
              :disabled="isDemo"
              :true-value="1"
              type="number"
              class="mx-3"/>

            <v-checkbox
              v-model="schema.works_on_holidays"
              :label="$t('workOnHolidays')"
              :error-messages="errorMessages.works_on_holidays"
              :value="0"
              :checked="1"
              color="success"
              class="mx-3"/>

            <v-checkbox
              v-model="schema.seasonal_short_time_work"
              :label="$t('seasonalShortTimeWork')"
              :error-messages="errorMessages.seasonal_short_time_work"
              :value="0"
              :checked="1"
              color="success"
              class="mx-3"/>

            <v-checkbox
              v-model="schema.is_part_time_worker"
              :label="$t('is_part_time_worker')"
              :error-messages="errorMessages.is_part_time_worker"
              :disabled="isDemo"
              :value="false"
              :checked="true"
              color="success"
              class="mx-3"/>

            <v-select
              :items="breaksItems"
              v-model="schema.break_minutes"
              :error-messages="errorMessages.break_minutes"
              :label="$t('breaksTime') + '*'"
              :rules="[$rules.isRequired]"
              item-text="text"
              item-value="value"
              class="mx-3"/>

            <v-subheader class="v-subheader-margin-left">{{ $t('chooseStyle') }}</v-subheader>

            <v-radio-group
              v-model="schema.styling_type_id"
              class="mx-3">
              <v-radio
                v-for="type in schemaTypes"
                :key="type.value"
                :label="type.text"
                :value="type.value"/>
            </v-radio-group>
          </v-flex>
          <v-flex xs6>

            <v-subheader class="v-subheader-margin-left">{{ $t('weekOptions') }}</v-subheader>

            <timesheet-date-time-selector
              :disabled="schema.is_part_time_worker"
              :day-names="weekDefault"
              :break-time="schema.break_minutes"
              :hours="schema.hours_week || 0"
              v-model="schema.days"
              @moreDaysNeeded="val => moreDaysNeeded = val"/>

            <date-picker
              v-model="started_working_at_company"
              :label="$t('started_working_at_company') + '*'"
              :rules="[$rules.isRequired]"
              :error-messages="errorMessages.started_working_at_company"/>

            <date-picker
              v-if="!employeeCreate"
              v-model="stopped_working_at_company"
              :label="$t('stopped_working_at_company')"
              :allowed-dates="allowedEndDates"
              :error-messages="errorMessages.stopped_working_at_company"/>
          </v-flex>
        </v-layout>
      </v-form>
    </v-card-text>
    <v-card-actions>

      <v-spacer/>

      <disabled-user-button v-if="employeeCreate && !isDemo">
        <v-btn
          slot-scope="{isDisabledForUser}"
          :disabled="isDisabledForUser"
          flat
          @click.native="$router.push({ name: 'employees' })"
        >
          {{ $t('back') }}
        </v-btn>
      </disabled-user-button>

      <disabled-user-button v-if="!isDemo">
        <v-btn
          slot-scope="{isDisabledForUser}"
          :loading="isLoading || loading"
          :disabled="isButtonDisabled || isDisabledForUser || !isFormValid"
          color="primary"
          @click.native="saveSchema()"
        >
          {{ employeeCreate ? $t('createEmployee') : $t( 'edit') }}
        </v-btn>
      </disabled-user-button>

      <v-tooltip
        :disabled="demoCount != 0"
        top>
        <template slot="activator">
          <v-btn
            v-if="isDemo"
            :loading="isLoading || loading"
            :disabled="isButtonDisabled || demoCount == 0"
            color="primary"
            @click="saveSchema"
          >
            <v-icon left>cloud_download</v-icon>
            {{ $t('download') }}
          </v-btn>
        </template>
        <span>{{ $t('pleaseSignUp') }}</span>
      </v-tooltip>

    </v-card-actions>
  </v-card>
</template>

<script>
import TimesheetApi from '@/api/TimesheetApi'
import EmployeeSettingsSchemaDay from './EmployeeSettingsSchemaDay.vue'
import { mapGetters, mapActions } from 'vuex'
import TimesheetDateTimeSelector from '@/components/routes/employees/timesheets/TimesheetDateTimeSelector'
import EmployeeSettingsSalaryGroup from './EmployeeSettingsSalaryGroup'
import DisabledUserButton from '@/components/global-components/disabledUserButton/disabledUserButton'
import MonthPicker from '@/components/global-components/inputs/MonthPicker'
import DatePicker from '@/components/global-components/inputs/DatePicker'

export default {
  name: 'EmployeeSettingsSchema',
  components: { DatePicker, DisabledUserButton, EmployeeSettingsSalaryGroup, TimesheetDateTimeSelector, EmployeeSettingsSchemaDay, MonthPicker },
  props: ['employeeCreate', 'errors', 'isEmployeeDataFilled', 'loading', 'isDemo', 'demoCount'],
  data () {
    return {
      isFormValid: false,
      isLoading: false,
      schema: null,
      moreDaysNeeded: false,
      errorMessages: {},
      schemaDefault: {
        employee_id: null,
        works_on_holidays: 0,
        seasonal_short_time_work: 0,
        break_minutes: null,
        hours_week: 0,
        salary_group_id: null,
        per_hour_calc: 0,
        monthly_wage: 0,
        is_part_time_worker: false,
        allowed_leave_days: 0,
        styling_type_id: 1,
        days: [],
        started_working_at_company: null,
        stopped_working_at_company: null
      },
      started_working_at_company: null,
      stopped_working_at_company: null,
      hourlyWage: null,
      breaksItems: [
        { text: this.$t('0min'), value: 0 },
        { text: this.$t('15min'), value: 15 },
        { text: this.$t('30min'), value: 30 },
        { text: this.$t('45min'), value: 45 },
        { text: this.$t('60min'), value: 60 },
        { text: this.$t('90min'), value: 90 },
        { text: this.$t('120min'), value: 120 }
      ],

      month: null
    }
  },
  computed: {
    ...mapGetters('employee', ['currentEmployee']),
    isPerHourCalcDisabled () {
      return !!this.schema.per_hour_calc
    },

    firstDayOfBegin () {
      if (this.schema.started_working_at_company) {
        return this.$moment(this.schema.started_working_at_company, 'YYYY-MM-DD')
      }
    },

    weekDefault () {
      return [
        this.$t('monday'),
        this.$t('tuesday'),
        this.$t('wednesday'),
        this.$t('thursday'),
        this.$t('friday'),
        this.$t('saturday'),
        this.$t('sunday')
      ]
    },

    schemaTypes () {
      return [
        { text: this.$t('default'), value: 1 },
        { text: this.$t('signEachDay'), value: 4 },
        { text: this.$t('signMonth'), value: 5 }
      ]
    },

    isButtonDisabled () {
      const isAnythingLoading = this.loading || this.isLoading || !this.schema // If schema loading
      const isEmployeeCreationDataFilled = this.employeeCreate ? this.isFormValid && this.isEmployeeDataFilled : true // If employee not creating no form data exist

      return isAnythingLoading || !isEmployeeCreationDataFilled || (this.moreDaysNeeded && !this.schema.is_part_time_worker)
    },
    schemaFormattedForBackend () {
      const schemaCopy = Object.assign({}, this.schema)
      schemaCopy.days = this.schema.days.filter(day => day.isActive)

      if (!schemaCopy.per_hour_calc) {
        delete schemaCopy.per_hour_calc
        delete schemaCopy.monthly_wage
        delete schemaCopy.salary_group_id
      } else {
        schemaCopy.hours_week = Math.floor(Number(schemaCopy.monthly_wage) / this.hourlyWage / 4.3)
      }

      return schemaCopy
    }
  },
  methods: {
    ...mapActions('employee', ['getEmployee']),

    onSalaryChange (value) {
      if (value) {
        this.schema.salary_group_id = value.id
        this.hourlyWage = value.hourlyWage
      }
    },

    clearForm () {
      this.$refs.schemaForm.reset()
    },
    async saveSchema () {
      if (!this.isFormValid) {
        return
      }

      if (this.started_working_at_company) {
        this.schema.started_working_at_company = this.$moment(this.started_working_at_company, 'YYYY-MM-DD').format('DD-MM-YYYY')
      } else {
        this.schema.started_working_at_company = null
      }

      if (this.stopped_working_at_company) {
        this.schema.stopped_working_at_company = this.$moment(this.stopped_working_at_company, 'YYYY-MM-DD').format('DD-MM-YYYY')
      } else {
        this.schema.stopped_working_at_company = null
      }

      if (this.employeeCreate) {
        const params = [this.schemaFormattedForBackend]
        if (this.month) {
          params.push(this.month)
        }
        this.$emit('onCreate', ...params) // if  in create employee then call create parent method
      } else {
        const response = await this.schemaCallsDecorator('edit', 'edit', this.schemaFormattedForBackend.id, this.schemaFormattedForBackend) // if not in create employee component then call edit inner method

        if (response.errors) {
          this.errorMessages = response
        } else {
          this.getEmployee({ companyId: this.currentEmployee.company_id, employeeId: this.currentEmployee.id, force: true }) // update store after success edit schema
          this.$notification(response)
        }
      }
    },

    allowedEndDates (val) {
      const $date = this.$moment(val, 'YYYY-MM-DD')
      let isAfterBegin = true

      if (this.schema.started_working_at_company) {
        isAfterBegin = this.firstDayOfBegin.diff($date, 'days') < 0
      }
      return isAfterBegin
    },

    // Allowed dates for demo generating
    allowedDates (val) {
      const $date = this.$moment(val, 'YYYY-MM').startOf('month')
      const $now = this.$moment().startOf('month')

      const isInPast = $date.diff($now, 'days') < 0
      return isInPast
    },
    async schemaCallsDecorator (rule, action, ...rest) {
      let response
      this.isLoading = true
      try {
        response = await TimesheetApi[`${action}Schema`](...rest)
      } catch (e) {
        response = e
        this.$notification(e)
      }
      this.isLoading = false
      return response
    }
  },
  watch: {
    errors: {
      deep: true,
      handler (val) {
        let errors = {}
        Object.keys(val).map(item => {
          const key = item.split('.')[1]
          const value = val[item]
          errors[key] = value
        })
        this.errorMessages = errors
      }
    },
    schema: {
      deep: true,
      handler () {
        this.$emit('clearErrors')
        this.errorMessages = {}
        if (this.schema.per_hour_calc) {
          this.schema.hours_week = Math.floor(Number(this.schema.monthly_wage) / this.hourlyWage / 4.3) || 0
        }
      }
    }
  },
  async created () {
    if (!this.employeeCreate && this.currentEmployee && this.currentEmployee.schema) {
      const schemaData = await this.schemaCallsDecorator('get', 'get', this.currentEmployee.id)
      this.schema = schemaData.data[0]
    } else {
      this.schema = this.schemaDefault
    }
    this.schema.days = this.weekDefault.map((dayName, index) => {
      const dayFound = this.schema.days.find(day => day.day_id === index + 1)
      if (dayFound) {
        dayFound.isActive = true
        return dayFound
      } else {
        return {
          isActive: false,
          day_id: index + 1,
          start_time: '08:00',
          end_time: null
        }
      }
    })

    this.hourlyWage = this.schema.hourlyWage
    this.started_working_at_company = this.schema.started_working_at_company
    this.stopped_working_at_company = this.schema.stopped_working_at_company
  },
  i18n: {
    messages: {
      en: {
        '0min': '0 minutes',
        '15min': '15 minutes',
        '30min': '30 minutes',
        '45min': '45 minutes',
        '60min': '60 minutes',
        '90min': '90 minutes',
        '120min': '120 minutes',
        baseOptions: 'Base options',
        breaksTime: 'Duration of usual break',
        chooseStyle: 'Timesheet style',
        create: 'Save settings',
        default: 'No signature',
        edit: 'Save settings',
        friday: 'Friday',
        headline: 'Default timesheet settings',
        hourlyWage: 'Hourly wage',
        hoursWeek: 'Hours per week',
        isRequired: 'Field is required',
        monday: 'Monday',
        monthlyWage: 'Monthly wage',
        perHourCalculation: 'Switch to salary per hour',
        saturday: 'Saturday',
        signEachDay: 'Sign every day',
        signMonth: 'Sign whole month',
        salaryGroupOptions: 'Salary group settings',
        sunday: 'Sunday',
        thursday: 'Thursday',
        tuesday: 'Tuesday',
        wednesday: 'Wednesday',
        weekOptions: 'Usual days of work',
        workOnHolidays: 'Employee works on holidays',
        allowedLeaveDays: 'Allowed leave days',
        download: 'Download',
        seasonalShortTimeWork: 'Seasonal short time work',
        pleaseSignUp: 'Please sign up',
        month: 'Month',
        is_part_time_worker: 'Without regular working days',
        createEmployee: 'Create employee',
        started_working_at_company: 'Contract start',
        stopped_working_at_company: 'Contract end'
      },
      tr: {
        '0min': '0 dakika',
        '15min': '15 dakika',
        '30min': '30 dakika',
        '45min': '45 dakika',
        '60min': '60 dakika',
        '90min': '90 dakika',
        '120min': '120 dakika',
        baseOptions: 'Temel işlemler',
        breaksTime: 'Genel mola süresi',
        chooseStyle: 'Zaman çizelgesi stili',
        create: 'Ayarları kaydet',
        default: 'İmza yok',
        edit: 'Ayarları kaydet',
        friday: 'Cuma',
        headline: 'Ön tanımlı zaman çizelgesi ayarları',
        hourlyWage: 'Saatlik ücret',
        hoursWeek: 'Haftalık saat',
        isRequired: 'Zorunlu alan',
        monday: 'Pazartesi',
        monthlyWage: 'Aylık ücret',
        perHourCalculation: 'Saatlik ücrete çevir',
        saturday: 'Cumartesi',
        signEachDay: 'Her günü imzala',
        signMonth: 'Tüm ayı imzala',
        salaryGroupOptions: 'Ücret grubu ayarları',
        sunday: 'Pazar',
        thursday: 'Perşembe',
        tuesday: 'Salı',
        wednesday: 'Çarşamba',
        weekOptions: 'Çalışma günleri',
        workOnHolidays: 'Çalışan tatilde çalışır',
        allowedLeaveDays: 'İzin verilen izin günleri',
        download: 'İndir',
        seasonalShortTimeWork: 'Sezonsal kısa süreli iş',
        pleaseSignUp: 'Lütfen kayıt olun',
        month: 'Ay',
        createEmployee: 'Çalışan oluştur',
        is_part_time_worker: 'Yarı zamanlı işçi',
        started_working_at_company: 'Sözleşme başlangıç',
        stopped_working_at_company: 'Sözleşmenin bitiş'
      },
      de: {
        '0min': 'Keine Pause',
        '15min': '15 Minuten',
        '30min': '30 Minuten',
        '45min': '45 Minuten',
        '60min': '60 Minuten',
        '90min': '90 Minuten',
        '120min': '120 Minuten',
        allowedLeaveDays: 'Urlaubstage im Jahr',
        baseOptions: 'Basisoptionen',
        breaksTime: 'Übliche Pause',
        chooseStyle: 'Dokumentenstil',
        create: 'Speichern',
        default: 'Keine Unterschrift',
        edit: 'Speichern',
        friday: 'Freitag',
        headline: 'Arbeitszeiteinstellungen',
        hourlyWage: 'Stundenlohn',
        hoursWeek: 'Stunden pro Woche',
        isRequired: 'Pflichtfeld',
        monday: 'Montag',
        monthlyWage: 'Monatslohn',
        perHourCalculation: 'Zur stundenbasierten Berechnung wechseln',
        salaryGroupOptions: 'Lohn/Gehalt',
        saturday: 'Samstag',
        signEachDay: 'Jeden Tag unterschreiben',
        signMonth: 'Kompletten Monat unterschreiben',
        sunday: 'Sonntag',
        thursday: 'Donnerstag',
        tuesday: 'Dienstag',
        wednesday: 'Mittwoch',
        weekOptions: 'Übliche arbeitstage',
        workOnHolidays: 'Arbeit am Feiertag?',
        back: 'zurück',
        download: 'Herunterladen',
        createEmployee: 'Mitarbeiter erstellen',
        seasonalShortTimeWork: 'Saison-Kurzarbeitergeld (ehem. Schlechtwettergeld)',
        pleaseSignUp: 'Bitte melden Sie sich an!',
        month: 'Monat',
        is_part_time_worker: 'Ohne feste Arbeitszeiten',
        started_working_at_company: 'Vertragsbeginn',
        stopped_working_at_company: 'Vertragsende'
      },
      ru: {
        '0min': '0 минут',
        '15min': '15 минут',
        '30min': '30 минут',
        '45min': '45 минут',
        '60min': '60 минут',
        '90min': '90 минут',
        '120min': '120 минут',
        baseOptions: 'Базовые настройки',
        breaksTime: 'Продолжительность обычного перерыва',
        chooseStyle: 'Вид табеля',
        create: 'Сохранить настройки',
        default: 'Без подписи',
        edit: 'Сохранить настройки',
        friday: 'Пятница',
        headline: 'Стандартные настройки табеля',
        hourlyWage: 'Почасовая заработная плата',
        hoursWeek: 'Часов в неделю',
        isRequired: 'Поле, обязательное для заполнения',
        monday: 'Понедельник',
        salaryGroupOptions: 'Настройка зарплатной группы',
        month: 'Месяц',
        monthlyWage: 'Ежемесячная заработная плата',
        perHourCalculation: 'Сменить на зарплату в час',
        personalSettings: 'Персональные данные',
        saturday: 'Суббота',
        signEachDay: 'Подписать каждый день',
        signMonth: 'Подписать весь месяц',
        sunday: 'Воскресенье',
        thursday: 'Четверг',
        tuesday: 'Вторник',
        wednesday: 'Среда',
        weekOptions: 'Обычные дни работы',
        seasonalShortTimeWork: 'Cезонная короткая работа',
        workOnHolidays: 'Сотрудник работает в праздничные дни',
        allowedLeaveDays: 'Разрешенные дни отпуска',
        is_part_time_worker: 'Работает неполный рабочий день',
        pleaseSignUp: 'Пожалуйста, зарегистрируйтесь',
        createEmployee: 'Создать сотрудника',
        started_working_at_company: 'Начала контракта\n',
        stopped_working_at_company: 'Конец контракта'
      }
    }
  }
}
</script>

<style scoped>
  .v-subheader-margin-left {
    margin-left: -1em;
  }
</style>
